import * as Popover from '@radix-ui/react-popover'
import { Command } from 'cmdk'
import { Check, ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { byRecency, splitByRecency } from '@/components/ui/recency'
import { cn } from '@/lib/utils'

export interface SearchSelectOption {
  value: string
  label: string
  /** Laatste activiteit (Y-m-d); bepaalt de "Recent"-groep. */
  lastActivityAt?: string | null
}

interface SearchSelectProps {
  value: string
  onValueChange: (value: string) => void
  options: SearchSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  className?: string
  disabled?: boolean
  id?: string
  ariaLabelledby?: string
  /** Venster voor de "Recent"-groep. Facturatie heeft een maand-/kwartaalritme. */
  recentWindowDays?: number
}

/**
 * Doorzoekbare single-select. De recency bepaalt alleen de ongezochte weergave
 * (groep "Recent" bovenaan, "Overig" eronder); zodra er getypt wordt filtert cmdk
 * over de VOLLEDIGE lijst, zodat een niet-recente optie net zo snel te vinden is.
 */
export function SearchSelect({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder = 'Zoeken…',
  emptyLabel = 'Niets gevonden.',
  className,
  disabled,
  id,
  ariaLabelledby,
  recentWindowDays = 90,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selected = options.find((o) => o.value === value) ?? null

  const { recent, rest } = useMemo(() => {
    const sorted = [...options].sort(byRecency((o) => o.lastActivityAt ?? null, (o) => o.label))
    return splitByRecency(sorted, (o) => o.lastActivityAt ?? null, recentWindowDays)
  }, [options, recentWindowDays])

  const hasRecency = options.some((o) => o.lastActivityAt)

  function pick(next: string) {
    onValueChange(next)
    setSearch('')
    setOpen(false)
  }

  function renderItem(option: SearchSelectOption) {
    return (
      <Command.Item
        key={option.value}
        value={option.label}
        onSelect={() => pick(option.value)}
        className="relative flex cursor-pointer select-none items-center rounded-md px-2.5 py-2 pr-7 text-sm text-ink aria-selected:bg-accent-600/10 aria-selected:text-accent-700"
      >
        {option.label}
        {option.value === value ? (
          <span className="absolute right-2 inline-flex items-center">
            <Check className="h-4 w-4 text-accent-600" />
          </span>
        ) : null}
      </Command.Item>
    )
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setSearch('')
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-labelledby={ariaLabelledby}
          disabled={disabled}
          className={cn(
            'inline-flex h-9 items-center justify-between gap-2 rounded-lg border border-hairline bg-surface px-3 text-sm',
            'focus:border-accent-500/50 focus:outline-hidden focus:ring-2 focus:ring-accent-500/25',
            'disabled:cursor-not-allowed disabled:opacity-60',
            selected ? 'text-ink' : 'text-ink-faint',
            className,
          )}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-faint" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-(--radix-popover-trigger-width) min-w-56 overflow-hidden rounded-card bg-surface shadow-pop animate-fade-in"
        >
          <Command loop>
            <div className="border-b border-hairline px-3">
              <Command.Input
                autoFocus
                value={search}
                onValueChange={setSearch}
                placeholder={searchPlaceholder}
                className="h-10 w-full bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-hidden"
              />
            </div>
            <Command.List className="max-h-64 overflow-y-auto p-1.5">
              <Command.Empty className="px-3 py-6 text-center text-sm text-ink-faint">{emptyLabel}</Command.Empty>

              {hasRecency && recent.length > 0 && rest.length > 0 ? (
                <>
                  <Command.Group
                    heading="Recent"
                    className="**:[[cmdk-group-heading]]:px-2.5 **:[[cmdk-group-heading]]:py-1 **:[[cmdk-group-heading]]:label-caps **:[[cmdk-group-heading]]:text-ink-faint"
                  >
                    {recent.map(renderItem)}
                  </Command.Group>
                  <Command.Group
                    heading="Overig"
                    className="mt-1 border-t border-hairline pt-1 **:[[cmdk-group-heading]]:px-2.5 **:[[cmdk-group-heading]]:py-1 **:[[cmdk-group-heading]]:label-caps **:[[cmdk-group-heading]]:text-ink-faint"
                  >
                    {rest.map(renderItem)}
                  </Command.Group>
                </>
              ) : (
                [...recent, ...rest].map(renderItem)
              )}

            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
